import {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';

import { DataExplorerViewItem } from './DataExplorerViewItem';
import type {
  DataExplorerViewCollectionLayout,
  DataExplorerViewCollectionProps,
  DataExplorerViewId,
  DataExplorerViewItemProps,
} from './types';
import { cn } from './utils';

function getItemsClassName(layout?: DataExplorerViewCollectionLayout) {
  switch (layout) {
    case 'list':
      return 'flex min-h-0 flex-col gap-3';
    case 'waterfall':
      return 'columns-1 gap-4 sm:columns-2 lg:columns-3';
    case 'grid':
    default:
      return 'grid min-h-0 gap-4';
  }
}

function getDefaultItemClassName(layout?: DataExplorerViewCollectionLayout) {
  if (layout === 'waterfall') {
    return 'mb-4 break-inside-avoid';
  }

  return undefined;
}

function defaultResolveItemId<TItem>(_item: TItem, index: number) {
  return String(index);
}

function resolveMeasuredGridMinColumnWidth(width: number | string) {
  if (typeof width === 'number') {
    return `${resolvePositiveNumber(width, 220)}px`;
  }

  return width;
}

function resolveMeasuredGridSpan(height: number, rowHeight: number, gap: number) {
  return Math.max(1, Math.ceil((height + gap) / (rowHeight + gap)));
}

function resolvePositiveNumber(value: number | undefined, fallback: number) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? value
    : fallback;
}

function resolveNonNegativeNumber(value: number | undefined, fallback: number) {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? value
    : fallback;
}

export function DataExplorerViewCollection<
  TItem = unknown,
  TContext = unknown,
  TViewId extends DataExplorerViewId = DataExplorerViewId,
>({
  items,
  activeViewId,
  context,
  capabilities,
  className,
  classNames,
  empty,
  loading,
  loadingNode,
  footer,
  overlay,
  layout = 'grid',
  scrollable = true,
  wrapItems = true,
  measuredGrid,
  itemsStyle,
  itemStyle,
  selectedIds = [],
  getItemId,
  itemDefinition,
  renderItem,
  getItemClassName,
  onOpenItem,
  onSelectionChange,
}: DataExplorerViewCollectionProps<TItem, TContext, TViewId>) {
  const resolvedItems = Array.isArray(items) ? items : [];
  const selectedIdSet = new Set(selectedIds);
  const resolveItemId = getItemId ?? defaultResolveItemId;
  const measuredGridOptions =
    measuredGrid === true
      ? {}
      : measuredGrid && typeof measuredGrid === 'object'
        ? measuredGrid
        : undefined;
  const measuredGridEnabled =
    layout === 'grid' && Boolean(measuredGridOptions?.enabled ?? measuredGrid);
  const measuredGridRowHeight = resolvePositiveNumber(
    measuredGridOptions?.rowHeight,
    4,
  );
  const measuredGridGap = resolveNonNegativeNumber(measuredGridOptions?.gap, 8);
  const measuredGridMinColumnWidth = measuredGridOptions?.minColumnWidth ?? '220px';
  const measuredElementsRef = useRef<Map<string, HTMLDivElement>>(new Map());
  const measuredCallbacksRef = useRef<
    Map<string, (node: HTMLDivElement | null) => void>
  >(new Map());
  const measuredSpansRef = useRef<Map<string, number>>(new Map());
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [, setMeasuredLayoutVersion] = useState(0);
  const resolveItemStyle =
    typeof itemStyle === 'function'
      ? (props: DataExplorerViewItemProps<TItem, TContext>) => itemStyle(props)
      : () => itemStyle;

  useEffect(() => {
    if (!measuredGridEnabled || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      let changed = false;

      for (const entry of entries) {
        const target = entry.target as HTMLDivElement;
        const id = target.dataset.dataExplorerItemId;
        if (!id) continue;

        const height = Math.ceil(entry.contentRect.height);
        const span = resolveMeasuredGridSpan(
          height,
          measuredGridRowHeight,
          measuredGridGap,
        );

        if (measuredSpansRef.current.get(id) !== span) {
          measuredSpansRef.current.set(id, span);
          changed = true;
        }
      }

      if (changed) {
        setMeasuredLayoutVersion((version) => version + 1);
      }
    });

    resizeObserverRef.current = resizeObserver;
    for (const element of measuredElementsRef.current.values()) {
      resizeObserver.observe(element);
    }

    return () => {
      resizeObserver.disconnect();
      if (resizeObserverRef.current === resizeObserver) {
        resizeObserverRef.current = null;
      }
    };
  }, [measuredGridEnabled, measuredGridGap, measuredGridRowHeight]);

  useEffect(() => {
    if (!measuredGridEnabled) {
      measuredElementsRef.current.clear();
      measuredCallbacksRef.current.clear();
      measuredSpansRef.current.clear();
      return;
    }

    const ids = new Set(resolvedItems.map((item, index) => resolveItemId(item, index)));
    let changed = false;

    for (const id of measuredSpansRef.current.keys()) {
      if (ids.has(id)) continue;
      measuredSpansRef.current.delete(id);
      changed = true;
    }

    for (const [id, element] of measuredElementsRef.current.entries()) {
      if (ids.has(id)) continue;
      resizeObserverRef.current?.unobserve(element);
      measuredElementsRef.current.delete(id);
    }

    for (const id of measuredCallbacksRef.current.keys()) {
      if (ids.has(id)) continue;
      measuredCallbacksRef.current.delete(id);
    }

    if (changed) {
      setMeasuredLayoutVersion((version) => version + 1);
    }
  }, [measuredGridEnabled, resolvedItems, resolveItemId]);

  const getMeasuredRef = useCallback((itemId: string) => {
    const cached = measuredCallbacksRef.current.get(itemId);
    if (cached) return cached;

    const callback = (node: HTMLDivElement | null) => {
      const previous = measuredElementsRef.current.get(itemId);
      if (previous && previous !== node) {
        resizeObserverRef.current?.unobserve(previous);
        measuredElementsRef.current.delete(itemId);
      }

      if (node) {
        node.dataset.dataExplorerItemId = itemId;
        measuredElementsRef.current.set(itemId, node);
        resizeObserverRef.current?.observe(node);
      }
    };

    measuredCallbacksRef.current.set(itemId, callback);
    return callback;
  }, []);

  const getMeasuredGridSpan = (props: DataExplorerViewItemProps<TItem, TContext>) => {
    const itemId = props.itemId;
    if (itemId) {
      const measured = measuredSpansRef.current.get(itemId);
      if (measured) return measured;
    }

    const estimate = measuredGridOptions?.estimateItemHeight;
    const rawEstimatedHeight =
      typeof estimate === 'function'
        ? estimate(props)
        : typeof estimate === 'number'
          ? estimate
          : 240;
    const estimatedHeight = resolvePositiveNumber(rawEstimatedHeight, 240);

    return resolveMeasuredGridSpan(
      estimatedHeight,
      measuredGridRowHeight,
      measuredGridGap,
    );
  };

  const resolveMeasuredItemsStyle = (): CSSProperties | undefined => {
    if (!measuredGridEnabled) return itemsStyle;

    return {
      ...itemsStyle,
      gap: measuredGridGap,
      gridAutoFlow: 'dense',
      gridAutoRows: measuredGridRowHeight,
      gridTemplateColumns: `repeat(auto-fill, minmax(${resolveMeasuredGridMinColumnWidth(measuredGridMinColumnWidth)}, 1fr))`,
    };
  };

  const renderStatus = () => {
    if (loading && loadingNode) {
      return (
        <div className={cn('flex items-center justify-center p-6 text-sm text-muted-foreground', classNames?.loading)}>
          {loadingNode}
        </div>
      );
    }

    if (!empty) {
      return null;
    }

    return (
      <div className={cn('flex items-center justify-center p-6 text-sm text-muted-foreground', classNames?.empty)}>
        {empty}
      </div>
    );
  };

  const handleSelectionChange = (
    targetItem: TItem,
    targetIndex: number,
    targetItemId: string,
    nextSelected: boolean,
  ) => {
    if (!onSelectionChange) {
      return;
    }

    const resolvedTargetId = getItemId ? getItemId(targetItem, targetIndex) : targetItemId;
    const nextSelectedIds = nextSelected
      ? Array.from(new Set([...selectedIds, resolvedTargetId]))
      : selectedIds.filter((id) => id !== resolvedTargetId);

    onSelectionChange(nextSelectedIds);
  };

  if (resolvedItems.length === 0 && !footer && !overlay) {
    return renderStatus();
  }

  return (
    <div
      className={cn('relative flex min-h-0 flex-1 flex-col overflow-hidden', classNames?.root, className)}
      data-view-id={activeViewId || undefined}
    >
      {resolvedItems.length > 0 ? (
        <div
          className={cn(getItemsClassName(layout), scrollable && 'overflow-auto', classNames?.items)}
          style={resolveMeasuredItemsStyle()}
        >
          {resolvedItems.map((item, index) => {
            const itemId = resolveItemId(item, index);
            const selected = selectedIdSet.has(itemId);
            const selectionChange = (targetItem: TItem, nextSelected: boolean) => {
              handleSelectionChange(targetItem, index, itemId, nextSelected);
            };
            const itemProps: DataExplorerViewItemProps<TItem, TContext> = {
              item,
              index,
              itemId,
              context,
              capabilities,
              itemDefinition,
              selected,
              onOpen: onOpenItem,
              onSelectionChange: selectionChange,
            };
            const itemClassName = cn(
              getDefaultItemClassName(layout),
              classNames?.item,
              getItemClassName?.({
                ...itemProps,
                className: classNames?.item,
              }),
            );
            const currentItemProps = {
              ...itemProps,
              className: itemClassName,
            };
            const renderedItem = renderItem ? renderItem(currentItemProps) : <DataExplorerViewItem {...currentItemProps} />;
            const resolvedStyle = resolveItemStyle(currentItemProps);
            const measuredStyle = measuredGridEnabled
              ? {
                  ...resolvedStyle,
                  gridRowEnd: `span ${getMeasuredGridSpan(currentItemProps)}`,
                }
              : resolvedStyle;

            if (!wrapItems) {
              if (!measuredGridEnabled) {
                return <Fragment key={itemId}>{renderedItem}</Fragment>;
              }

              return (
                <div key={itemId} className={itemClassName} style={measuredStyle}>
                  <div ref={getMeasuredRef(itemId)}>{renderedItem}</div>
                </div>
              );
            }

            return (
              <div key={itemId} className={itemClassName} style={measuredStyle}>
                {measuredGridEnabled ? (
                  <div ref={getMeasuredRef(itemId)}>{renderedItem}</div>
                ) : (
                  renderedItem
                )}
              </div>
            );
          })}
        </div>
      ) : (
        renderStatus()
      )}

      {loading && loadingNode && resolvedItems.length > 0 ? (
        <div className={cn('shrink-0 p-3 text-sm text-muted-foreground', classNames?.loading)}>{loadingNode}</div>
      ) : null}

      {footer ? <div className={cn('shrink-0', classNames?.footer)}>{footer}</div> : null}

      {overlay ? <div className={cn('absolute inset-0 z-10', classNames?.overlay)}>{overlay}</div> : null}
    </div>
  );
}
